import { Moon, Sun } from "lucide-react";
import { useTheme } from "@/hooks/use-theme";
import { cn } from "@/lib/cn";

interface ThemeToggleProps {
  /**
   * Variante visual.
   * - `icon-button`: botón cuadrado con borde (para top bars o controles).
   * - `menu-item`: full-width row para usar dentro de un dropdown.
   */
  variant?: "icon-button" | "menu-item";
  className?: string;
}

/**
 * Toggle de tema dark/light.
 *
 * Usa el hook `useTheme` (con persistencia en localStorage). El `index.html`
 * pre-aplica la clase `dark` antes del bundle para evitar flicker.
 */
export function ThemeToggle({
  variant = "icon-button",
  className,
}: ThemeToggleProps) {
  const { isDark, toggle } = useTheme();
  const Icon = isDark ? Sun : Moon;
  const label = isDark ? "Cambiar a modo claro" : "Cambiar a modo oscuro";

  if (variant === "menu-item") {
    return (
      <button
        type="button"
        onClick={toggle}
        aria-label={label}
        className={cn(
          "flex items-center w-full gap-2.5 rounded-lg px-3 py-2 text-sm",
          "text-text-secondary hover:text-text-primary hover:bg-surface-2",
          "transition-colors",
          className,
        )}
      >
        <Icon size={16} strokeWidth={1.5} />
        <span className="flex-1 text-left">
          {isDark ? "Modo claro" : "Modo oscuro"}
        </span>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={label}
      className={cn(
        "inline-flex items-center justify-center",
        "w-9 h-9 rounded-lg",
        "border border-border-default bg-transparent",
        "text-text-secondary hover:text-text-primary hover:bg-surface-2",
        "transition-colors",
        className,
      )}
    >
      <Icon size={16} strokeWidth={1.5} />
    </button>
  );
}
